// Where a focus is, and how to point at it from the edge of the screen
// (slice 13; design/08 §5): one concept, two representations. On screen,
// the highlight itself; off it, an arrow at the edge of the viewport aimed
// at it, which clicking travels to. Pure arithmetic over the board view, so
// it is graded here rather than in a browser.
import { axisPositions, cellBounds, type BoardView, type Cell, type Grid } from '@dragons-table/vtt-shared';

export interface Viewport { width: number; height: number }
export interface Rect { x: number; y: number; width: number; height: number }

/** A focus's box in board pixels: the bounds of its cells over the fitted grid. */
export function focusRect(cells: Cell[], grid: Grid, extent: Viewport): Rect {
  const xs = axisPositions(grid, extent, 'x');
  const ys = axisPositions(grid, extent, 'y');
  const b = cellBounds(cells);
  const at = (p: number[], i: number) => p[Math.max(0, Math.min(p.length - 1, i))]!;
  const x0 = at(xs, b.i0); const x1 = at(xs, b.i1 + 1); const y0 = at(ys, b.j0); const y1 = at(ys, b.j1 + 1);
  return { x: x0, y: y0, width: Math.max(1, x1 - x0), height: Math.max(1, y1 - y0) };
}

/** Each cell's rectangle in board pixels, for the highlight. */
export function cellRects(cells: Cell[], grid: Grid, extent: Viewport): Rect[] {
  const xs = axisPositions(grid, extent, 'x');
  const ys = axisPositions(grid, extent, 'y');
  const at = (p: number[], i: number) => p[Math.max(0, Math.min(p.length - 1, i))]!;
  return cells.map((c) => { const x = at(xs, c.i); const y = at(ys, c.j); return { x, y, width: Math.max(1, at(xs, c.i + 1) - x), height: Math.max(1, at(ys, c.j + 1) - y) }; });
}

export interface Arrow {
  /** Where the arrow sits on the viewport, in screen pixels from the host's top-left. */
  x: number;
  y: number;
  /** Which way it points, in degrees clockwise from "right". */
  angle: number;
}

const MARGIN = 18;

/**
 * The arrow for a focus off the screen, or null while any of it is on
 * screen: from the viewport's centre towards the focus's centre, stopped
 * at the edge with a margin so the arrow stays inside the host.
 */
export function arrowFor(rect: Rect, view: BoardView, viewport: Viewport): Arrow | null {
  const sx0 = (rect.x - view.x) * view.zoom; const sy0 = (rect.y - view.y) * view.zoom;
  const sx1 = sx0 + rect.width * view.zoom; const sy1 = sy0 + rect.height * view.zoom;
  const onScreen = sx1 > 0 && sx0 < viewport.width && sy1 > 0 && sy0 < viewport.height;
  if (onScreen) return null;
  const cx = viewport.width / 2; const cy = viewport.height / 2;
  const tx = (sx0 + sx1) / 2; const ty = (sy0 + sy1) / 2;
  const dx = tx - cx; const dy = ty - cy;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  // Scale the direction to the inner rectangle's edge.
  const hw = cx - MARGIN; const hh = cy - MARGIN;
  const k = Math.min(hw / Math.max(1e-6, Math.abs(dx)), hh / Math.max(1e-6, Math.abs(dy)));
  return { x: cx + dx * k, y: cy + dy * k, angle };
}
